import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ClipboardList, Plus, FileText } from "lucide-react";
import { SuiteEyebrow, SuiteBadge } from "@/components/suite";

/**
 * C3-B.2.8 · Portale dipendente · Assenze e Permessi.
 * Riusa il sistema di richieste esistente (/api/leave-requests/mine + dialog).
 */
export default function EmployeeLeaves() {
  const [reqs, setReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [companyName, setCompanyName] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/leave-requests/mine");
      setReqs(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    (async () => {
      try {
        const { data } = await api.get("/employees", { params: { status: "all" } });
        const rec = Array.isArray(data) && data.length > 0 ? data[0] : null;
        if (rec?.company_name) setCompanyName(rec.company_name);
      } catch (_) { /* ignore */ }
    })();
  }, []);

  const stateTone = (s) => ({
    approved: "success", accettata: "success",
    rejected: "error", respinta: "error",
    pending: "warning", in_attesa: "warning", requested: "warning", created: "warning",
    cancelled: "neutral", annullata: "neutral",
  }[(s || "").toLowerCase()] || "neutral");

  const canCancel = (r) => ["pending", "in_attesa", "requested", "created"].includes((r.status || "").toLowerCase());

  const cancelReq = async (id) => {
    if (!confirm("Annullare la richiesta?")) return;
    try {
      await api.post(`/leave-requests/${id}/cancel`);
      toast.success("Richiesta annullata");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6 max-w-[1200px]" data-testid="employee-leaves-page">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <SuiteEyebrow tone="primary" icon={ClipboardList}>Assenze e Permessi</SuiteEyebrow>
        <h1 className="text-3xl font-bold tracking-tight mt-1 text-suite-neutral-800"
            style={{ fontFamily: "Manrope" }}>
          Le mie richieste
        </h1>
        {companyName && (
          <p className="text-sm mt-1 text-suite-neutral-600" data-testid="employee-leaves-company">
            Azienda di appartenenza: <span className="font-semibold">{companyName}</span>
          </p>
        )}
      </motion.div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-suite-neutral-500">
          {reqs.length} richiesta/e totali
        </div>
        <Link to="/?open_leave=1" data-testid="employee-leaves-new-btn"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-lg text-white font-semibold text-[13px]"
              style={{ background: "#4CAF50" }}>
          <Plus size={16} /> Nuova richiesta
        </Link>
      </div>

      {loading ? (
        <div className="text-sm text-suite-neutral-500">Caricamento…</div>
      ) : reqs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-suite-neutral-200 bg-white p-10 text-center"
             data-testid="employee-leaves-empty">
          <FileText className="w-8 h-8 mx-auto text-suite-neutral-300" />
          <div className="mt-3 text-sm text-suite-neutral-500">
            Nessuna richiesta al momento.
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-suite-neutral-200 overflow-hidden bg-white"
             data-testid="employee-leaves-list">
          <table className="w-full text-sm">
            <thead className="bg-suite-neutral-50">
              <tr className="text-left text-[11px] uppercase tracking-wider text-suite-neutral-500">
                <th className="px-4 py-3">Causale</th>
                <th className="px-4 py-3">Dal</th>
                <th className="px-4 py-3">Al</th>
                <th className="px-4 py-3">Ore</th>
                <th className="px-4 py-3">Nota</th>
                <th className="px-4 py-3">Stato</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {reqs.map((r) => (
                <tr key={r.id} className="border-t border-suite-neutral-100"
                    data-testid={`employee-leaves-row-${r.id}`}>
                  <td className="px-4 py-3 font-medium text-suite-neutral-800">{r.cause || "—"}</td>
                  <td className="px-4 py-3 text-suite-neutral-600">{r.date_from || "—"}</td>
                  <td className="px-4 py-3 text-suite-neutral-600">{r.date_to || "—"}</td>
                  <td className="px-4 py-3 text-suite-neutral-600">{r.hours ?? "—"}</td>
                  <td className="px-4 py-3 text-suite-neutral-500 max-w-[280px] truncate"
                      title={r.note || r.reason || ""}>
                    {r.note || r.reason || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <SuiteBadge tone={stateTone(r.status)}>{r.status || "—"}</SuiteBadge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canCancel(r) && (
                      <button
                        onClick={() => cancelReq(r.id)}
                        className="text-[12px] text-suite-error hover:underline"
                        data-testid={`employee-leaves-cancel-${r.id}`}
                      >
                        Annulla
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {null}
    </div>
  );
}
